/**
 * Bidirectional adapter between the rigging-canvas DiagramFile shape and the
 * SafetyDiagram API canvas_data shape that pdf.py / viewsets.py expect.
 *
 * Backend schema (per element):
 *   { id, type, legendNumber, label, metadata, x, y, ... }
 *
 * Canvas schema (DiagramFile):
 *   { schemaVersion, savedAt, elements: DrawingElement[], calibration, nextLegendNumber }
 */

import type { DiagramFile } from './persistence'
import { DIAGRAM_SCHEMA_VERSION } from './persistence'
import type {
  DrawingElement,
  FreedrawElement,
  GearElement,
  RopeElement,
  BasicShapeElement,
  GearType,
  RopeRole,
  Calibration,
} from './types'
import { getGearSubtype } from './gear-subtypes'

// ─── Backend element type strings ───────────────────────────────────────────

export type BackendElementType =
  | 'anchor'
  | 'pulley'
  | 'descender'
  | 'carabiner'
  | 'load'
  | 'technician'
  | 'rope_line'
  | 'ground_edge'
  | 'building_edge'
  | 'i_beam'
  | 'structure_block'
  | 'rope_grab'
  | 'casualty'
  | 'stretcher'
  | 're_anchor'
  | 'shape'
  | 'freedraw'

export interface BackendElement {
  id: string
  type: BackendElementType
  legendNumber: number | null
  label: string
  metadata: Record<string, string | number | null>
  // Position preserved for round-trip
  x: number
  y: number
  width?: number
  height?: number
  rotation?: number
  // Canvas-native fields needed for lossless round-trip
  _canvas?: Record<string, unknown>
}

export interface BackendCanvasData {
  schemaVersion: number
  savedAt: string
  elements: BackendElement[]
  calibration?: Calibration | null
  nextLegendNumber?: number
}

// ─── Canvas GearType → Backend type mapping ─────────────────────────────────

const GEAR_TO_BACKEND: Record<GearType, BackendElementType> = {
  'anchor': 'anchor',
  'pulley': 'pulley',
  'descender': 'descender',
  'carabiner': 'carabiner',
  'load': 'load',
  'technician': 'technician',
  'deviation': 'anchor',
  'rope-grab': 'rope_grab',
  're-anchor': 're_anchor',
  'casualty': 'casualty',
  'stretcher': 'stretcher',
  'i-beam-h': 'i_beam',
  'i-beam-v': 'i_beam',
  'edge': 'ground_edge',
  'edge-left': 'building_edge',
  'edge-right': 'building_edge',
}

const BACKEND_TO_GEAR: Record<string, GearType> = {
  'anchor': 'anchor',
  'pulley': 'pulley',
  'descender': 'descender',
  'carabiner': 'carabiner',
  'load': 'load',
  'technician': 'technician',
  'rope_grab': 'rope-grab',
  're_anchor': 're-anchor',
  'casualty': 'casualty',
  'stretcher': 'stretcher',
  'i_beam': 'i-beam-h',
  'ground_edge': 'edge',
  'building_edge': 'edge-left',
  'structure_block': 'edge',
}

// ─── To Backend (save) ──────────────────────────────────────────────────────

function gearMetadata(el: GearElement): Record<string, string | number | null> {
  const meta: Record<string, string | number | null> = {}
  const st = getGearSubtype(el.subtype)

  switch (el.gearType) {
    case 'anchor':
    case 'deviation':
    case 're-anchor':
      if (st) meta.anchorType = st.label
      break
    case 'pulley':
      if (st) {
        meta.makeModel = st.label
        meta.efficiencyTier = st.efficiency != null && st.efficiency >= 0.9
          ? 'high'
          : st.efficiency != null && st.efficiency >= 0.8
          ? 'medium'
          : 'low'
      }
      break
    case 'descender':
      if (st) meta.makeModel = st.label
      break
    default:
      break
  }

  if (el.subtype) meta._subtype = el.subtype
  return meta
}

function ropeMetadata(el: RopeElement): Record<string, string | number | null> {
  const meta: Record<string, string | number | null> = {}
  if (el.role) meta.ropeType = el.role
  if (el.ropeDiameterMm) meta.diameter = String(el.ropeDiameterMm)
  if (el.role) meta.role = el.role
  return meta
}

function convertGearToBackend(el: GearElement): BackendElement {
  const backendType = GEAR_TO_BACKEND[el.gearType] ?? 'anchor'
  return {
    id: el.id,
    type: backendType,
    legendNumber: el.legendNumber ?? null,
    label: el.label ?? '',
    metadata: gearMetadata(el),
    x: el.x,
    y: el.y,
    width: el.width,
    height: el.height,
    rotation: el.rotation,
    _canvas: {
      kind: 'gear',
      gearType: el.gearType,
      parentElementId: el.parentElementId,
      parentSnapPointId: el.parentSnapPointId,
      subtype: el.subtype,
    },
  }
}

function convertRopeToBackend(el: RopeElement): BackendElement {
  return {
    id: el.id,
    type: 'rope_line',
    legendNumber: null,
    label: '',
    metadata: ropeMetadata(el),
    x: 0,
    y: 0,
    _canvas: {
      kind: 'rope',
      start: el.start,
      end: el.end,
      contacts: el.contacts,
      color: el.color,
      diameter: el.diameter,
      role: el.role,
      sagMultiplier: el.sagMultiplier,
      autoSag: el.autoSag,
      ropeDiameterMm: el.ropeDiameterMm,
    },
  }
}

function convertShapeToBackend(el: BasicShapeElement): BackendElement {
  let backendType: BackendElementType = 'shape'
  if (el.shapeType === 'i-beam') backendType = 'i_beam'

  return {
    id: el.id,
    type: backendType,
    legendNumber: null,
    label: el.text ?? '',
    metadata: { shapeType: el.shapeType },
    x: el.x,
    y: el.y,
    width: el.width,
    height: el.height,
    rotation: el.rotation,
    _canvas: {
      kind: 'shape',
      shapeType: el.shapeType,
      stroke: el.stroke,
      fill: el.fill,
      fontSize: el.fontSize,
    },
  }
}

/**
 * Convert a canvas DiagramFile into the backend canvas_data shape.
 */
function convertFreedrawToBackend(el: FreedrawElement): BackendElement {
  return {
    id: el.id,
    type: 'freedraw',
    legendNumber: null,
    label: '',
    metadata: {},
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    rotation: 0,
    _canvas: {
      kind: 'freedraw',
      points: el.points,
      stroke: el.stroke,
      strokeWidth: el.strokeWidth,
    },
  }
}

export function toBackendSchema(diagram: DiagramFile): BackendCanvasData {
  const elements: BackendElement[] = diagram.elements.map((el) => {
    switch (el.kind) {
      case 'gear':
        return convertGearToBackend(el)
      case 'rope':
        return convertRopeToBackend(el)
      case 'shape':
        return convertShapeToBackend(el)
      case 'freedraw':
        return convertFreedrawToBackend(el)
    }
  })

  return {
    schemaVersion: diagram.schemaVersion,
    savedAt: diagram.savedAt,
    elements,
    calibration: diagram.calibration ?? null,
    nextLegendNumber: diagram.nextLegendNumber,
  }
}

// ─── From Backend (load) ────────────────────────────────────────────────────

function backendToGear(el: BackendElement): GearElement | null {
  const canvas = el._canvas as Record<string, unknown> | undefined
  const gearType: GearType = canvas?.gearType
    ? (canvas.gearType as GearType)
    : BACKEND_TO_GEAR[el.type] ?? 'anchor'

  return {
    id: el.id,
    kind: 'gear',
    gearType,
    x: el.x,
    y: el.y,
    width: el.width ?? 48,
    height: el.height ?? 48,
    rotation: el.rotation ?? 0,
    label: el.label || undefined,
    legendNumber: el.legendNumber ?? undefined,
    parentElementId: canvas?.parentElementId as string | undefined,
    parentSnapPointId: canvas?.parentSnapPointId as string | undefined,
    subtype: (canvas?.subtype as string) ?? (el.metadata?._subtype as string) ?? undefined,
  }
}

function backendToRope(el: BackendElement): RopeElement | null {
  const canvas = el._canvas as Record<string, unknown> | undefined
  if (!canvas) {
    // Minimal fallback for ropes created in the old editor
    return {
      id: el.id,
      kind: 'rope',
      start: { mode: 'free', x: el.x, y: el.y, fallsToGround: false },
      end: { mode: 'free', x: el.x + 100, y: el.y, fallsToGround: false },
      contacts: [],
      color: '#dc2626',
      diameter: 3,
      role: (el.metadata?.role as RopeRole) ?? 'working',
      ropeDiameterMm: el.metadata?.diameter ? Number(el.metadata.diameter) : undefined,
    }
  }

  return {
    id: el.id,
    kind: 'rope',
    start: canvas.start as RopeElement['start'],
    end: canvas.end as RopeElement['end'],
    contacts: (canvas.contacts as RopeElement['contacts']) ?? [],
    color: (canvas.color as string) ?? '#dc2626',
    diameter: (canvas.diameter as number) ?? 3,
    role: (canvas.role as RopeRole) ?? undefined,
    sagMultiplier: canvas.sagMultiplier as number | undefined,
    autoSag: canvas.autoSag as boolean | undefined,
    ropeDiameterMm: canvas.ropeDiameterMm as number | undefined,
  }
}

function backendToShape(el: BackendElement): BasicShapeElement | null {
  const canvas = el._canvas as Record<string, unknown> | undefined
  return {
    id: el.id,
    kind: 'shape',
    shapeType: (canvas?.shapeType as BasicShapeElement['shapeType']) ?? 'rect',
    x: el.x,
    y: el.y,
    width: el.width ?? 100,
    height: el.height ?? 100,
    rotation: el.rotation ?? 0,
    text: el.label || undefined,
    fontSize: canvas?.fontSize as number | undefined,
    stroke: (canvas?.stroke as string) ?? '#000000',
    fill: canvas?.fill as string | undefined,
  }
}

const ROPE_BACKEND_TYPES = new Set<string>(['rope_line', 'rope_segment'])
const SHAPE_ONLY_TYPE = 'shape'

/**
 * Convert backend canvas_data into a canvas DiagramFile for the editor.
 */
export function fromBackendSchema(data: unknown): DiagramFile | null {
  if (!data || typeof data !== 'object') return null
  const payload = data as Record<string, unknown>

  const rawElements = payload.elements
  if (!Array.isArray(rawElements)) return null

  const elements: DrawingElement[] = []

  for (const raw of rawElements) {
    if (!raw || typeof raw !== 'object') continue
    const el = raw as BackendElement

    if (!el.id || !el.type) continue

    if (el.type === 'freedraw' || (el._canvas as Record<string, unknown> | undefined)?.kind === 'freedraw') {
      const canvas = el._canvas as Record<string, unknown> | undefined
      if (canvas) {
        elements.push({
          id: el.id,
          kind: 'freedraw',
          points: (canvas.points as number[]) ?? [],
          stroke: (canvas.stroke as string) ?? '#111827',
          strokeWidth: (canvas.strokeWidth as number) ?? 3,
        })
      }
    } else if (ROPE_BACKEND_TYPES.has(el.type)) {
      const rope = backendToRope(el)
      if (rope) elements.push(rope)
    } else if (el.type === SHAPE_ONLY_TYPE || (el._canvas as Record<string, unknown> | undefined)?.kind === 'shape') {
      const shape = backendToShape(el)
      if (shape) elements.push(shape)
    } else {
      const gear = backendToGear(el)
      if (gear) elements.push(gear)
    }
  }

  return {
    schemaVersion: typeof payload.schemaVersion === 'number' ? payload.schemaVersion : DIAGRAM_SCHEMA_VERSION,
    savedAt: typeof payload.savedAt === 'string' ? payload.savedAt : new Date().toISOString(),
    elements,
    calibration: payload.calibration as Calibration | null ?? null,
    nextLegendNumber: typeof payload.nextLegendNumber === 'number' ? payload.nextLegendNumber : undefined,
  }
}
